import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type PreviewContextValue = {
  on: boolean;
  setOn: (on: boolean) => void;
  activeCount: number;
  register: () => void;
  unregister: () => void;
};

const PreviewContext = createContext<PreviewContextValue | null>(null);

export const PreviewProvider = ({ children }: { children: ReactNode }) => {
  if (!import.meta.env.VITE_IS_PREVIEW) {
    return children;
  }
  const [on, setOn] = useState(true);
  const [activeCount, setActiveCount] = useState(0);

  const register = useCallback(() => setActiveCount((c) => c + 1), []);
  const unregister = useCallback(() => setActiveCount((c) => c - 1), []);

  const value = useMemo(
    () => ({ on, setOn, activeCount, register, unregister }),
    [on, activeCount, register, unregister],
  );

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
};

export const usePreview = (): boolean => {
  const ctx = useContext(PreviewContext);
  const register = ctx?.register;
  const unregister = ctx?.unregister;

  useEffect(() => {
    if (!import.meta.env.VITE_IS_PREVIEW || !register || !unregister) return;
    register();
    return unregister;
  }, [register, unregister]);

  if (!ctx) return false;
  return ctx?.on ?? false;
};

export const usePreviewToggle = (): {
  on: boolean;
  setOn?: (on: boolean) => void;
  shouldShow: boolean;
} => {
  const ctx = useContext(PreviewContext);
  if (!ctx) return { on: false, shouldShow: false };
  return { on: ctx.on, setOn: ctx.setOn, shouldShow: !!ctx.activeCount };
};
